import { action, makeObservable, observable } from 'mobx';
import { IApplePayDomain, IApplePayIntegrationSettings, IApplePaySettings } from '../types/applePay';
import { defaultApplePaySettings } from '../constants/applePay';
import client from '~/services/client';
import logger from '~/services/logger';
import { IResponse } from '~/types/main';
import { BaseStore } from '~/view/screens/SettingsScreen/store/BaseStore';
import { localeStore } from '~/mobx/localesStore';
import { toastSuccess } from '~/utils/toasts';

export class ApplePaySettingsStore extends BaseStore {
  constructor() {
    super();
    makeObservable(this);
  }

  private url: string = 'v3/integrations/apple-pay';

  private idGet: string = 'getApplePaySettingsStore';
  private idUpdate: string = 'updateApplePaySettingsStore';

  @observable public isLoading: boolean = false;
  @observable public isEdited: boolean = false;

  @observable public settings: IApplePaySettings = defaultApplePaySettings;

  @action public mount = async (): Promise<void> => {
    await this.get();
  };

  @action public unMount = (): void => {
    this.isLoading = false;
    this.isEdited = false;
    this.settings = defaultApplePaySettings;
  };

  @action private get = async (): Promise<void> => {
    try {
      this.abort(this.idGet);

      this.actionBeforeGetRequest();

      const response: IResponse<IApplePayIntegrationSettings> = await client.get(this.url, this.getParams(this.idGet));

      this.settings = response.data?.settings ?? defaultApplePaySettings;

      this.actionAfterGetRequest();
    } catch (error: any) {
      if (this.isAborted(error)) return;

      this.actionAfterGetRequest();
      logger.error({ error, place: this.idGet, showModal: true });
    }
  };

  @action public update = async (): Promise<void> => {
    try {
      this.abort(this.idUpdate);

      this.actionBeforeUpdateRequest();

      await client.post(this.url, this.settings, this.getParams(this.idUpdate));

      this.actionAfterUpdateRequest();

      toastSuccess({ message: localeStore.t('toast_settings_saved') });
    } catch (error: any) {
      if (this.isAborted(error)) return;

      this.actionAfterUpdateRequest();
      logger.error({ error, place: this.idUpdate, showModal: true });
    }
  };

  @action public changeSettingHandler = (settingsPart: Partial<IApplePaySettings>): void => {
    this.settings = { ...this.settings, ...settingsPart };
    this.isEdited = true;
  };

  @action public changeDomainHandler = (domainName: string, domainPart: Partial<IApplePayDomain>): void => {
    this.settings.domains = this.settings.domains.map((domain) => {
      if (domain.domain === domainName) {
        this.isEdited = true;
        return { ...domain, ...domainPart };
      }

      return domain;
    });
  };

  @action private actionBeforeGetRequest = (): void => {
    this.isLoading = true;
    this.isEdited = false;
    this.settings = defaultApplePaySettings;
  };

  @action private actionAfterGetRequest = (): void => {
    this.isLoading = false;
    this.isEdited = false;
  };

  @action private actionBeforeUpdateRequest = (): void => {
    this.isLoading = true;
  };

  @action private actionAfterUpdateRequest = (): void => {
    this.isLoading = false;
    this.isEdited = false;
  };
}

const applePaySettingsStore = new ApplePaySettingsStore();

export default applePaySettingsStore;
